import type { ReactNode, Ref } from 'react'
import { Icon } from '@/components/readiness/primitives'

// ── Toolbar layout rule (Iteration 4 §3) ────────────────────────────────────
// One layout for the Overview, Tracker and Catalogue toolbars: every control
// is 42px; the search grows, filter triggers (FilterSelect) keep their
// intrinsic width, toggles are pills, never native checkboxes; the result
// count and the view toggle close the row. At 1280px and above the widths
// keep it to one row; below, it wraps.

// `innerRef` lets a locked preview anchor its gate on the filter row (I6 POC §5.2).
export function Toolbar({ children, className = '', testId, innerRef }: { children: ReactNode; className?: string; testId?: string; innerRef?: Ref<HTMLDivElement> }) {
  return <div ref={innerRef} className={`rd-toolbar ${className}`} data-testid={testId}>{children}</div>
}

export function ToolbarSearch({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="rd-toolbar-search">
      <span className="sr-only">{label}</span>
      <Icon name="search" size={15} className="rd-toolbar-search-icon" />
      <input className="rd-input" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

export function TogglePill({ pressed, onChange, children, testId }: { pressed: boolean; onChange: (v: boolean) => void; children: ReactNode; testId?: string }) {
  return (
    <button type="button" aria-pressed={pressed} className={`rd-trigger rd-toggle ${pressed ? 'rd-on' : ''}`} onClick={() => onChange(!pressed)} data-testid={testId}>
      {pressed && <Icon name="check" size={15} />}
      {children}
    </button>
  )
}

export function ResultCount({ children, testId }: { children: ReactNode; testId?: string }) {
  return <span className="rd-result-count" data-testid={testId}>{children}</span>
}

export function ViewToggle<T extends string>({ value, options, onChange, label, testId }: { value: T; options: Array<{ value: T; icon: 'list' | 'grid'; label: string; testId?: string }>; onChange: (v: T) => void; label: string; testId?: string }) {
  return (
    <span className="rd-view-toggle" role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} aria-label={o.label} title={o.label} onClick={() => onChange(o.value)} data-testid={o.testId}>
          <Icon name={o.icon} size={16} />
        </button>
      ))}
    </span>
  )
}
